/**
 * URL validation for the public report form.
 *
 * Two rules, both enforced before anything leaves the browser:
 *  1. Only the designated link field may contain a URL — see `containsUrl`.
 *  2. That field only accepts links whose host belongs to a supported platform
 *     — see `getSupportedPlatformUrl`.
 *
 * Supported networks are configured in one place: SUPPORTED_PLATFORMS in
 * `src/constants`.
 */

import { SUPPORTED_PLATFORMS } from '../constants';

/** Every registrable domain of every supported platform. */
export const SUPPORTED_PLATFORM_DOMAINS = SUPPORTED_PLATFORMS.flatMap((platform) => platform.domains);

/** Platform names for user-facing copy, e.g. "Facebook, Twitter, and TikTok". */
export const SUPPORTED_PLATFORMS_LABEL = (() => {
  const names = SUPPORTED_PLATFORMS.map((platform) => platform.name);
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
})();

// cw_proxy rejects anything longer, so there is no point in sending it.
const MAX_URL_LENGTH = 2000;

const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;

/** One DNS label: letters/digits, hyphens allowed inside. */
const HOST_LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';

/**
 * Suffixes that make a protocol-less string read as a host. Deliberately a
 * short list: prose with a missing space ("wrong.Really") must not be mistaken
 * for a link, while pasted spam ("spam.com", "bit.ly/x") still is. Links to the
 * supported platforms are matched by domain below, whatever their suffix.
 */
const RECOGNIZED_TLDS = [
  'com', 'net', 'org', 'io', 'co', 'info', 'biz', 'gov', 'edu', 'xyz', 'top',
  'club', 'shop', 'site', 'online', 'ly', 'gl', 'cc', 'tv', 'ru', 'cn', 'ir',
  'de', 'fr', 'uk', 'il',
];

const escapeForRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A protocol-qualified ("https://…") or www-prefixed link anywhere in the text. */
const EXPLICIT_URL_PATTERN = /(?:[a-z][a-z0-9+.-]*:\/\/|\bwww\.)\S/i;

/** A host written without its protocol, e.g. "facebook.com/p/1" or "bit.ly/x". */
const BARE_HOST_PATTERN = new RegExp(
  `\\b(?:${HOST_LABEL}\\.)*(?:${SUPPORTED_PLATFORM_DOMAINS.map(escapeForRegExp).join('|')}`
  + `|${HOST_LABEL}\\.(?:${RECOGNIZED_TLDS.join('|')}))(?![a-z0-9-])`,
  'i',
);

/** An email address is not a link; drop those before looking for bare hosts. */
const EMAIL_PATTERN = /\b[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+/gi;

/**
 * The host must be the platform domain itself or a subdomain of it. Matching on
 * a substring would let "https://evil.com/facebook.com/x" or
 * "https://notfacebook.com" through.
 * @param {string} hostname - Hostname as parsed by URL
 * @returns {boolean}
 */
const isSupportedHostname = (hostname) => {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return SUPPORTED_PLATFORM_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
};

/**
 * Whether a value contains something that reads as a URL, with or without a
 * protocol. Used to keep links out of every field but the designated one.
 * @param {string} value - Raw field value
 * @returns {boolean}
 */
export const containsUrl = (value) => {
  const text = String(value ?? '');
  if (!text.trim()) return false;
  if (EXPLICIT_URL_PATTERN.test(text)) return true;
  return BARE_HOST_PATTERN.test(text.replace(EMAIL_PATTERN, ' '));
};

/**
 * Validate the designated link field by parsing it and checking its hostname
 * against the supported platforms.
 * @param {string} value - Raw field value
 * @returns {string|null} The parsed link, protocol included, or null when it is
 *   not a parseable http(s) URL on a supported platform.
 */
export const getSupportedPlatformUrl = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || /\s/.test(trimmed) || trimmed.length > MAX_URL_LENGTH) return null;

  // A link pasted without its protocol is still a link: assume https so it can
  // be parsed. cw_proxy requires a scheme, so the parsed form is what gets sent.
  const candidate = SCHEME_PATTERN.test(trimmed) ? trimmed : `https://${trimmed}`;

  let url;
  try {
    url = new URL(candidate);
  } catch (error) {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return isSupportedHostname(url.hostname) ? url.href : null;
};

/**
 * Error text for a link found in a field that may not hold one.
 * @param {string} fieldLabel - Label of the offending field
 * @param {string} linkFieldLabel - Label of the field that does accept links
 * @returns {string}
 */
export const getLinkNotAllowedError = (fieldLabel, linkFieldLabel) =>
  `Links are only allowed in the "${linkFieldLabel}" field. Please remove the link from "${fieldLabel}".`;
